<script setup lang="ts">
  import { onBeforeMount, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import { ComboSelect } from '@ui/components/combo-select';

  import PriorityIndicator from '@/components/common/PriorityIndicator.vue';

  interface Props {
    multiple?: boolean;
    options: Record<string, any>[];
    queryName?: string;
  }

  interface PriorityOption {
    label: string;
    value: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    multiple: false,
    queryName: 'priority',
  });

  const emit = defineEmits<{
    (e: 'update', value: string[]): void;
  }>();

  const route = useRoute();
  const selectedPriority = ref<PriorityOption[]>([]);

  onBeforeMount(() => {
    const priorityQuery = route.query?.priority;

    if (priorityQuery) {
      const priorityArray = Array.isArray(priorityQuery) ? priorityQuery : [priorityQuery];

      selectedPriority.value = priorityArray
        .map((val) => {
          return props.options?.find((option) => option.value === val);
        })
        .filter((option) => option !== undefined) as PriorityOption[];
    }
  });

  defineExpose({
    selectedPriority,
  });
</script>

<template>
  <ComboSelect
    v-if="options?.length"
    v-model="selectedPriority"
    :options="options"
    searchable
    variant="filter"
    placeholder="Priority"
    :multiple="multiple"
    search-placeholder="Filter by priority..."
    option-identifier="label"
    @update:model-value="
      (priority) =>
        emit(
          'update',
          Array.isArray(priority)
            ? priority?.map((c) => c.value)
            : priority
              ? [priority.value]
              : [],
        )
    "
  >
    <template #filter-label>
      <i class="fas fa-circle text-3xs" />
      <span> Priority </span>
    </template>

    <template #option="{ option }">
      <div class="flex items-center w-full whitespace-nowrap truncate gap-x-2">
        <PriorityIndicator :priority="option.value" />
        <span>{{ option.label }}</span>
      </div>
    </template>
  </ComboSelect>
</template>
